<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="multi-stage-process-timeline-title" :field="fields?.title" tag="h2" />
      <JssRichText class="multi-stage-process-timeline-subtitle" :field="fields?.subtitle" />
      <div class="multi-stage-process-timeline-steps" v-if="timelineSteps.length > 0">
        <div
          class="multi-stage-process-timeline-steps-step"
          v-for="(step, index) in timelineSteps"
          :key="index"
        >
          <JssRichText :field="step" />
        </div>
      </div>
      <div class="multi-stage-process-timeline-cards" v-if="cardList?.length > 0">
        <div
          class="multi-stage-process-timeline-cards-card"
          v-for="(card, index) in fields?.cardList"
          :key="index"
        >
          <h3 class="multi-stage-process-timeline-cards-card-title">
            <JssRichText :field="card?.fields?.title" tag="h3" />
          </h3>
          <div class="multi-stage-process-timeline-cards-card-desc">
            <JssRichText :field="card?.fields?.desc" />
          </div>
          <AdaptiveImage
            class="multi-stage-process-timeline-cards-card-image"
            :field="card?.fields?.image"
          />
        </div>
      </div>
      <JssRichText class="multi-stage-process-timeline-description" :field="fields?.desc" />
      <div class="multi-stage-process-timeline-desc" v-if="descList?.length > 0">
        <div
          class="multi-stage-process-timeline-desc-item"
          v-for="(item, index) in fields?.descList"
          :key="index"
        >
          <JssRichText :field="item?.fields?.text" />
        </div>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { computed } from 'vue';
import utils from '@/utils';

export default {
  name: 'MultiStageProcessTimeline',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const timelineSteps = computed(() => [
      props.fields.stepOneText || '',
      props.fields.stepTwoText || '',
      props.fields.stepThreeText || '',
      props.fields.stepFourText || '',
    ]);

    const cardList = computed(() => {
      const items = props.fields.cardList?.value || [];
      return items.map((item) => ({
        title: item.title?.value || '',
        desc: item.desc?.value
          ? item.desc.value
              .split('|')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        image: item.image?.value?.src || '',
      }));
    });

    const desc = computed(() => props.fields.desc?.value || '');

    const descList = computed(() => {
      const items = props.fields.descList?.value || [];
      return items.map((item) => ({
        text: item.text?.value || '',
      }));
    });

    const rootClass = computed(() =>
      utils.overall.baseComClass(props.fields, 'multi-stage-process-timeline')
    );

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    return {
      timelineSteps,
      cardList,
      desc,
      descList,
      rootClass,
      rootStyle,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.multi-stage-process-timeline {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    text-align: center;
  }

  &-subtitle {
    font-size: 14px;
    line-height: 1.6;
    color: $black;
    margin-top: 8px;
    text-align: center;
  }

  &-steps {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 24px;
    margin-top: 24px;

    &-step {
      background: #f5f9f4;
      width: 100%;
      min-height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #c4d8c0;
      border-radius: 4px;
      font-weight: 600;
      color: $blue-night;
      padding: 12px 16px;

      span,
      p {
        font-size: 14px;
        font-weight: 600;
        color: $blue-night;
        white-space: nowrap;
        margin: 0;
        text-align: center;
      }
    }
  }

  &-cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 16px;
    margin-bottom: 24px;

    &-card {
      background: linear-gradient(to top left, rgba(107, 145, 113, 0.3), transparent);
      border-radius: 8px;
      padding: 20px;
      border: 1px solid #e8f0e6;
      position: relative;

      &-title,
      &-desc {
        position: relative;
        z-index: 2;
      }

      &-title {
        font-size: 18px;
        font-weight: 700;
        color: $black;
        margin-bottom: 12px;
      }

      &-desc {
        font-size: 14px;

        p {
          position: relative;
          margin: 0 0 8px 0;

          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      &-image {
        position: absolute;
        right: 0;
        bottom: 0;
        width: 64px;
        height: 93px;
      }
    }
  }

  &-description {
    font-size: 12px;
    color: #6b7c67;
    text-align: center;
    margin-bottom: 24px;
    padding: 0 8px;
  }

  &-desc {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    &-item {
      background: #efeadf;
      border-radius: 8px;
      padding: 14px 20px;
      font-size: 12px;
      font-weight: 500;
      color: $grey-black;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 48px;
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-subtitle {
      font-size: 18px;
    }

    &-steps {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      position: relative;
      padding: 0;
      margin-bottom: 30px;
      margin-top: 40px;

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        height: 2px;
        background-color: #c4d8c0;
        transform: translateY(-50%);
      }

      &-step {
        position: relative;
        z-index: 1;
        background: $white;
        width: 114px;
        height: 61px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #c4d8c0;
        border-radius: 4px;
        background-color: #f5f9f4;
        font-weight: 600;
        color: $blue-night;

        span,
        p {
          font-size: 14px;
          font-weight: 600;
          color: $blue-night;
          white-space: nowrap;
          margin: 0;
          text-align: center;
        }
      }
    }

    &-cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 32px;
      margin-top: 24px;
      margin-bottom: 30px;
      padding: 0 60px;

      &-card {
        background: linear-gradient(to top left, rgba(107, 145, 113, 0.3), transparent);
        border-radius: 8px;
        padding: 24px;
        border: 1px solid #e8f0e6;
        position: relative;

        &-title,
        &-desc {
          position: relative;
          z-index: 2;
        }

        &-title {
          font-size: 24px;
          font-weight: 700;
          color: $black;
          margin-bottom: 16px;
        }

        &-desc {
          font-size: 16px;

          p {
            position: relative;
            margin: 0 0 8px 0;

            &:last-child {
              margin-bottom: 0;
            }
          }
        }

        &-image {
          position: absolute;
          right: 0;
          bottom: 0;
          width: 96px;
          height: 139px;
        }
      }
    }

    &-description {
      font-size: 14px;
      color: #6b7c67;
      text-align: center;
      margin-bottom: 30px;
      padding: 0 10%;
    }

    &-desc {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 32px;
      padding: 0 60px;

      &-item {
        background: #efeadf;
        border-radius: 8px;
        padding: 18px 30px;
        font-size: 14px;
        font-weight: 500;
        color: $grey-black;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 56px;
      }
    }
  }
}
</style>
